import React, { useEffect, useMemo, useState } from 'react';
import authService from '../../api/authService';

const IframeIntegrationPage = () => {
  const [profile, setProfile] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [generatedUrl, setGeneratedUrl] = useState('');

  const [sourceType, setSourceType] = useState('team');
  const [selectedApiKeyId, setSelectedApiKeyId] = useState('');
  const [selectedTeamMemberId, setSelectedTeamMemberId] = useState('');

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const meRes = await authService.me();
        if (!meRes.success) return;

        setProfile(meRes.userInfo);
        if (meRes.userInfo?.role !== 'owner') {
          return;
        }

        const [memberRes, apiKeyRes] = await Promise.all([
          authService.queryTeamMembers({ page: 1, limit: 100, search: '', status: 'active' }),
          authService.getApiKeys()
        ]);

        if (memberRes.success) {
          setTeamMembers(memberRes.data || []);
        }

        if (apiKeyRes.success) {
          setApiKeys(apiKeyRes.data || []);
        }
      } catch (error) {
        setMessage(error.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const selectedApiKey = useMemo(
    () => apiKeys.find((item) => String(item._id) === String(selectedApiKeyId)) || null,
    [apiKeys, selectedApiKeyId]
  );

  const selectedTeamMember = useMemo(
    () => teamMembers.find((item) => String(item._id) === String(selectedTeamMemberId)) || null,
    [teamMembers, selectedTeamMemberId]
  );

  const handleGenerate = () => {
    setMessage('');
    setGeneratedUrl('');

    if (!selectedApiKey?.publicKey) {
      selectedApiKey.publicKey = "apikey"
      //setMessage('Please select an API key.');
      //return;
    }

    if (sourceType === 'team' && !selectedTeamMember?.email) {
      setMessage('Please select a team member.');
      return;
    }

    const baseUrl = `${window.location.origin}/embed?apiKey=${encodeURIComponent(selectedApiKey.publicKey)}`;
    const url = sourceType === 'team'
      ? `${baseUrl}&teamEmail=${encodeURIComponent(selectedTeamMember.email)}`
      : baseUrl;

    setGeneratedUrl(url);
  };

  if (loading) {
    return <div className="p-5 text-center">Loading iframe generator...</div>;
  }

  if (profile?.role !== 'owner') {
    return (
      <div className="container-fluid py-4">
        <div className="alert alert-warning mb-0">Only the workspace owner can generate iframe URLs.</div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <div className="mb-4">
        <h4 className="fw-700 mb-1">Iframe URL Generator</h4>
        <div className="text-muted">Simple format: team based uses `apiKey` + `teamEmail`, custom uses only `apiKey`.</div>
      </div>

      {message && (
        <div className="alert alert-info">{message}</div>
      )}

      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label fw-600">Type</label>
              <select
                className="form-select"
                value={sourceType}
                onChange={(e) => {
                  setSourceType(e.target.value);
                  setGeneratedUrl('');
                  setMessage('');
                }}
              >
                <option value="team">Team Member Based</option>
                <option value="custom">Custom</option>
              </select>
            </div>

            <div className="col-md-4">
              <label className="form-label fw-600">API Key</label>
              <select
                className="form-select"
                value={selectedApiKeyId}
                onChange={(e) => {
                  setSelectedApiKeyId(e.target.value);
                  setGeneratedUrl('');
                  setMessage('');
                }}
              >
                <option value="">Select API key</option>
                {apiKeys.map((apiKey) => (
                  <option key={String(apiKey._id)} value={String(apiKey._id)}>
                    {apiKey.name} ({apiKey.prefix})
                  </option>
                ))}
              </select>
            </div>

            {sourceType === 'team' && (
              <div className="col-md-4">
                <label className="form-label fw-600">Team Member</label>
                <select
                  className="form-select"
                  value={selectedTeamMemberId}
                  onChange={(e) => {
                    setSelectedTeamMemberId(e.target.value);
                    setGeneratedUrl('');
                    setMessage('');
                  }}
                >
                  <option value="">Select team member</option>
                  {teamMembers.map((member) => (
                    <option key={String(member._id)} value={String(member._id)}>
                      {member.name} ({member.email})
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="mt-4">
            <button className="btn btn-primary" onClick={handleGenerate}>
              Generate URL
            </button>
          </div>
        </div>
      </div>

      {generatedUrl && (
        <div className="card border-0 shadow-sm">
          <div className="card-body">
            <label className="form-label fw-600">Generated URL</label>
            <textarea className="form-control" rows="4" value={generatedUrl} readOnly />
            {sourceType === 'team' && selectedTeamMember && (
              <div className="text-muted mt-2">
                Team Member: <strong>{selectedTeamMember.email}</strong>
                {selectedTeamMember.roleTemplateName ? ` | Role: ${selectedTeamMember.roleTemplateName}` : ''}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default IframeIntegrationPage;
